<template>
  <div
    id="djl_events"
    aria-labelledby="events-panel-title"
    role="region"
  >
    <h2
      id="events-panel-title"
      class="visually-hidden"
    >
      Event Details
    </h2>

    <div
      ref="srAnnouncement"
      class="visually-hidden"
      aria-live="assertive"
      aria-atomic="true"
    />

    <button
      type="button"
      class="event-nav-button event-nav-up"
      :disabled="!hasPreviousEvent"
      @click="previousEvent"
    >
      <span class="visually-hidden">Previous Event</span>
    </button>

    <div
      class="event-details-content"
      aria-live="polite"
      aria-atomic="true"
    >
      <div class="events-container">
        <div
          v-for="(occurrence, index) in appointments"
          :key="occurrence.id || index"
          class="event-detail-item"
          :class="{ 'active': index === currentOccurrenceIndex }"
          :aria-hidden="index !== currentOccurrenceIndex"
        >
          <label class="event-day-month">
            {{ getMonthFromDate(occurrence.start_at) }}
          </label>
          <label class="event-day-num">
            {{ getDayNumFromDate(occurrence.start_at) }}
          </label>
          <label class="event-day">
            {{ getWeekdayFromDate(occurrence.start_at) }}
          </label>
          <label class="event-time">
            {{ getTimeFromDate(occurrence.start_at) }} -
            {{ getTimeFromDate(occurrence.end_at) }}
          </label>
          <div
            class="event-title"
            role="heading"
            aria-level="3"
          >
            {{ occurrence.name }}
          </div>
          <p class="event-des">
            {{ getTruncatedDescription(occurrence.description) }}
          </p>

          <button
            class="event-details-signup button button--secondary"
            @click="() => eventSignUpAndDetailsClick(occurrence)"
          >
            Event Details & Signup
          </button>
        </div>
      </div>

      <h2
        v-if="noEventsForDate || appointments.length === 0"
        class="no-events-message"
      >
        No Events Found
      </h2>
    </div>

    <button
      type="button"
      class="event-nav-button event-nav-down"
      :disabled="!hasNextEvent"
      @click="nextEvent"
    >
      <span class="visually-hidden">Next Event</span>
    </button>
  </div>
</template>

<script>
import { attachFocusTrap } from '../utils/focus_trap';

export default {
  name: 'DjlEvents',
  props: {
    events: {
      type: Array,
      required: true,
    },
  },

  data() {
    let siteTimeZone = null;
    try {
      const serverDataEl = document.getElementById('serverData');
      const serverData = serverDataEl ? JSON.parse(serverDataEl.textContent) : {};
      siteTimeZone = serverData.timeZone || null;
    } catch (_e) { /* siteTimeZone stays null, browser default used */ }
    return {
      currentOccurrenceIndex: 0,
      appointments: [],
      noEventsForDate: false,
      siteTimeZone,
    };
  },

  computed: {
    hasPreviousEvent() {
      return this.currentOccurrenceIndex > 0;
    },
    hasNextEvent() {
      return this.currentOccurrenceIndex < this.appointments.length - 1;
    },
  },

  mounted() {
    this.reload(this.events);

    if (!window.djlCalendar) window.djlCalendar = {};
    const djl = window.djlCalendar;
    djl.events = {
      previousEvent: this.previousEvent,
      nextEvent: this.nextEvent,
      focusByDate: this.focusByDate,
      focusByEvent: this.focusByEvent,
      reload: this.reload,
      getCurrentIndex: this.getCurrentIndex,
      openDetailsModalForCurrentEvent: this.openDetailsModalForCurrentEvent,
    };
    djl.previousEventHandler = () => this.previousEvent();
    djl.nextEventHandler = () => this.nextEvent();
    djl.focusByDateHandler = (date) => this.focusByDate(date);
  },

  methods: {
    getMonthFromDate(dateStr) {
      if (!dateStr) return '';
      const opts = { month: 'long' };
      if (this.siteTimeZone) opts.timeZone = this.siteTimeZone;
      return new Date(dateStr).toLocaleString('en-US', opts);
    },

    getDayNumFromDate(dateStr) {
      if (!dateStr) return '';
      const opts = { day: '2-digit' };
      if (this.siteTimeZone) opts.timeZone = this.siteTimeZone;
      return new Date(dateStr).toLocaleString('en-US', opts);
    },

    getWeekdayFromDate(dateStr) {
      if (!dateStr) return '';
      const opts = { weekday: 'long' };
      if (this.siteTimeZone) opts.timeZone = this.siteTimeZone;
      return new Date(dateStr).toLocaleString('en-US', opts);
    },

    getTimeFromDate(dateStr) {
      if (!dateStr) return '';
      const opts = { hour: 'numeric', minute: 'numeric' };
      if (this.siteTimeZone) opts.timeZone = this.siteTimeZone;
      return new Date(dateStr).toLocaleString('en-US', opts);
    },

    getTruncatedDescription(description) {
      if (!description) return '';
      let strippedDescription = description.replace(/<\/?[^>]+(>|$)/g, '');
      strippedDescription = strippedDescription.replace(/\s+/g, ' ').trim();
      return strippedDescription.length > 150
        ? `${strippedDescription.substring(0, 150)}...`
        : strippedDescription;
    },

    previousEvent() {
      const prevOnSameDate = this.getPreviousEventOnSameDate();

      if (prevOnSameDate !== -1) {
        this.currentOccurrenceIndex = prevOnSameDate;
        this.updateCalendarHighlight();
        this.updateNavigationState();
        this.announceSidebarDetails();
        return;
      }

      if (this.currentOccurrenceIndex > 0) {
        this.currentOccurrenceIndex -= 1;
        this.updateCalendarHighlight();
        this.updateNavigationState();
        this.announceSidebarDetails();
      }
    },

    getCurrentIndex() {
      return this.currentOccurrenceIndex;
    },

    announceSidebarDetails() {
      this.$nextTick(() => {
        const el = this.$refs.srAnnouncement;
        if (!el) return;
        if (this.appointments.length === 0) {
          el.textContent = 'No events.';
          return;
        }
        const occurrence = this.appointments[this.currentOccurrenceIndex];
        const n = this.currentOccurrenceIndex + 1;
        const m = this.appointments.length;
        const parts = [];
        if (occurrence?.name) parts.push(occurrence.name);
        if (occurrence?.start_at) {
          const dateStr = `${this.getWeekdayFromDate(occurrence.start_at)}, ${this.getMonthFromDate(occurrence.start_at)} ${this.getDayNumFromDate(occurrence.start_at)}`;
          const timeStr = occurrence.end_at
            ? `${this.getTimeFromDate(occurrence.start_at)} to ${this.getTimeFromDate(occurrence.end_at)}`
            : this.getTimeFromDate(occurrence.start_at);
          parts.push(`${dateStr} at ${timeStr}`);
        }
        const desc = this.getTruncatedDescription(occurrence?.description);
        if (desc) parts.push(desc);
        parts.push(`Event ${n} of ${m}.`);
        el.textContent = parts.join('. ');
      });
    },

    nextEvent() {
      const nextOnSameDate = this.getNextEventOnSameDate();

      if (nextOnSameDate !== -1) {
        this.currentOccurrenceIndex = nextOnSameDate;
        this.updateCalendarHighlight();
        this.updateNavigationState();
        this.announceSidebarDetails();
        return;
      }

      if (this.currentOccurrenceIndex < this.appointments.length - 1) {
        this.currentOccurrenceIndex += 1;
        this.updateCalendarHighlight();
        this.updateNavigationState();
        this.announceSidebarDetails();
      }
    },

    openDetailsModalForCurrentEvent() {
      const occurrence = this.appointments[this.currentOccurrenceIndex];
      if (occurrence) {
        this.eventSignUpAndDetailsClick(occurrence);
      }
    },

    eventSignUpAndDetailsClick(occurrence) {
      const eventId = occurrence.event_id;
      const occurrenceId = occurrence.id;
      const restoreFocusTo = document.activeElement;

      if (!eventId) return;

      let url = `/events/${eventId}?source=delaware_home_page`;
      if (occurrenceId) {
        url += `&occurrence_id=${occurrenceId}`;
      }

      if (occurrence.isRegistered) {
        url += '&is_registered=true';
      }

      const eventDetailsBtn = document.querySelector('.event-detail-item.active .event-details-signup');
      if (eventDetailsBtn) {
        eventDetailsBtn.classList.add('loading');
        eventDetailsBtn.disabled = true;
      }

      fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((html) => {
          if (!window.djlCalendar) window.djlCalendar = {};
          const djl = window.djlCalendar;
          if (typeof djl.cleanup === 'function') {
            djl.cleanup();
            djl.cleanup = null;
          }

          const detailsContainer = document.getElementById(
            'event-details-container',
          );
          const modalElement = document.getElementById('DjlEventDetailsModal');

          if (!detailsContainer || !modalElement) {
            throw new Error('Modal elements not found');
          }

          detailsContainer.innerHTML = html;
          modalElement.style.display = 'block';
          document.body.classList.add('event-details-modal-open');

          this.setupEventRegistration(occurrence);
          djl.restoreFocus = () => {
            if (restoreFocusTo && document.body.contains(restoreFocusTo)) {
              restoreFocusTo.focus();
            } else {
              eventDetailsBtn?.focus();
            }
          };

          const closeModal = () => {
            if (typeof djl.restoreFocus === 'function') {
              djl.restoreFocus();
              djl.restoreFocus = null;
            }
            if (typeof djl.cleanup === 'function') {
              djl.cleanup();
              djl.cleanup = null;
            }
            modalElement.style.display = 'none';
            document.body.classList.remove('event-details-modal-open');
          };
          djl.closeModal = closeModal;

          const overlayClose = (e) => {
            if (e.target === modalElement) {
              closeModal();
            }
          };
          modalElement.addEventListener('click', overlayClose);

          const handleEscape = (e) => {
            if (e.key === 'Escape') {
              closeModal();
              document.removeEventListener('keydown', handleEscape);
            }
          };
          document.addEventListener('keydown', handleEscape);

          djl.cleanup = () => {
            modalElement.removeEventListener('click', overlayClose);
            document.removeEventListener('keydown', handleEscape);
            if (typeof this._focusTrapCleanup === 'function') {
              this._focusTrapCleanup();
              this._focusTrapCleanup = null;
            }
          };

          const closeBtn = document.getElementById('closeDjlEventDetailsModal');
          if (closeBtn) {
            closeBtn.focus();
            this._setupFocusTrap(modalElement);
            closeBtn.onclick = closeModal;
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console -- intentional log for failed event details fetch
          console.error('Event details fetch failed:', err);
          const container = document.querySelector('.event-details-content')
            || document.querySelector('.events-container');
          if (container) {
            const errEl = document.createElement('div');
            errEl.className = 'registration-message registration-message--error';
            errEl.textContent = 'Unable to load event details. Please try again.';
            container.insertBefore(errEl, container.firstChild);
            setTimeout(() => { if (errEl.parentNode) errEl.remove(); }, 5000);
          }
        })
        .finally(() => {
          if (eventDetailsBtn) {
            eventDetailsBtn.classList.remove('loading');
            eventDetailsBtn.disabled = false;
          }
        });
    },

    reload(flattenedOccurrences) {
      if (!Array.isArray(flattenedOccurrences)) {
        this.appointments = [];
      } else {
        this.appointments = [...flattenedOccurrences].sort((a, b) => (a.start_at || '').localeCompare(b.start_at || ''),
        );
        this.setInitialEventIndex();
      }

      this.noEventsForDate = false;
      this.$nextTick(() => {
        this.updateCalendarHighlight();
        this.updateNavigationState();
      });
    },

    setInitialEventIndex() {
      if (this.appointments.length === 0) return;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const todayStr = today.toISOString().substring(0, 10);

      const todayIndex = this.appointments.findIndex(
        (occurrence) => occurrence.start_at
          && occurrence.start_at.substring(0, 10) === todayStr,
      );

      if (todayIndex !== -1) {
        this.currentOccurrenceIndex = todayIndex;
        return;
      }

      let closestFutureIndex = -1;
      let closestFutureDiff = Infinity;

      let closestPastIndex = -1;
      let closestPastDiff = Infinity;

      this.appointments.forEach((occurrence, index) => {
        if (!occurrence.start_at) return;

        const eventDate = new Date(occurrence.start_at);
        const diff = eventDate - today;

        if (diff >= 0) {
          if (diff < closestFutureDiff) {
            closestFutureDiff = diff;
            closestFutureIndex = index;
          }
        } else {
          const absDiff = Math.abs(diff);
          if (absDiff < closestPastDiff) {
            closestPastDiff = absDiff;
            closestPastIndex = index;
          }
        }
      });

      if (closestFutureIndex !== -1) {
        this.currentOccurrenceIndex = closestFutureIndex;
      } else if (closestPastIndex !== -1) {
        this.currentOccurrenceIndex = closestPastIndex;
      } else {
        this.currentOccurrenceIndex = 0;
      }
    },

    focusByEvent(calendarEvent) {
      const model = calendarEvent?.extendedProps?.model;
      if (!model) {
        // Fallback: no model attached, use date-based lookup
        const targetStartAt = calendarEvent?.startStr;
        if (targetStartAt) this.focusByDate(targetStartAt.substring(0, 10));
        return;
      }

      // Match by occurrence id first, then by event_id + start_at for robustness
      let matchIndex = this.appointments.findIndex(
        (occ) => occ.id != null && occ.id === model.id,
      );
      if (matchIndex === -1) {
        matchIndex = this.appointments.findIndex(
          (occ) => occ.event_id === model.event_id
                 && occ.start_at === model.start_at,
        );
      }

      if (matchIndex !== -1) {
        this.currentOccurrenceIndex = matchIndex;
        this.noEventsForDate = false;
        this.$forceUpdate();
        this.$nextTick(() => {
          this.updateCalendarHighlight();
          this.updateNavigationState();
          this.announceSidebarDetails();
        });
      } else {
        // Occurrence not in sidebar data — fall back to date
        const targetStartAt = calendarEvent?.startStr;
        if (targetStartAt) this.focusByDate(targetStartAt.substring(0, 10));
      }
    },

    focusByDate(date) {
      const eventsOnDate = this.appointments.filter(
        (occurrence) => occurrence.start_at?.startsWith(date),
      );

      if (eventsOnDate.length > 0) {
        if (
          this.currentOccurrenceIndex >= 0
          && this.appointments[this.currentOccurrenceIndex]?.start_at?.startsWith(
            date,
          )
        ) {
          this.noEventsForDate = false;
        } else {
          const firstIndex = this.appointments.findIndex(
            (occurrence) => occurrence.start_at?.startsWith(date),
          );

          this.currentOccurrenceIndex = firstIndex;
          this.noEventsForDate = false;
        }
      } else {
        this.noEventsForDate = true;

        if (this.appointments.length > 0) {
          this.currentOccurrenceIndex = 0;
        }
      }

      this.$forceUpdate();
      this.$nextTick(() => {
        this.updateNavigationState();
        this.announceSidebarDetails();
      });
    },

    getNextEventOnSameDate() {
      const currentEvent = this.appointments[this.currentOccurrenceIndex];
      if (!currentEvent || !currentEvent.start_at || currentEvent.id == null) return -1;

      const currentDate = currentEvent.start_at.substring(0, 10);

      const sameDay = this.appointments.filter(
        (event) => event.start_at?.substring(0, 10) === currentDate,
      );

      if (sameDay.length <= 1) return -1;

      const currentPosInSameDay = sameDay.findIndex(
        (event) => event.id === currentEvent.id && event.start_at === currentEvent.start_at,
      );

      if (currentPosInSameDay >= sameDay.length - 1) return -1;

      const nextEventOnSameDay = sameDay[currentPosInSameDay + 1];

      return this.appointments.findIndex(
        (event) => event.id === nextEventOnSameDay.id
          && event.start_at === nextEventOnSameDay.start_at,
      );
    },

    getPreviousEventOnSameDate() {
      const currentEvent = this.appointments[this.currentOccurrenceIndex];
      if (!currentEvent || !currentEvent.start_at || currentEvent.id == null) return -1;

      const currentDate = currentEvent.start_at.substring(0, 10);

      const sameDay = this.appointments.filter(
        (event) => event.start_at?.substring(0, 10) === currentDate,
      );

      if (sameDay.length <= 1) return -1;

      const currentPosInSameDay = sameDay.findIndex(
        (event) => event.id === currentEvent.id && event.start_at === currentEvent.start_at,
      );

      if (currentPosInSameDay <= 0) return -1;

      const prevEventOnSameDay = sameDay[currentPosInSameDay - 1];

      return this.appointments.findIndex(
        (event) => event.id === prevEventOnSameDay.id
          && event.start_at === prevEventOnSameDay.start_at,
      );
    },

    updateNavigationState() {
      this.$nextTick(() => {
        const prevBtn = document.querySelector(
          '.event-nav-button.event-nav-up',
        );
        const nextBtn = document.querySelector(
          '.event-nav-button.event-nav-down',
        );

        if (prevBtn) {
          prevBtn.disabled = !this.hasPreviousEvent;
          prevBtn.setAttribute('aria-disabled', !this.hasPreviousEvent);
        }

        if (nextBtn) {
          nextBtn.disabled = !this.hasNextEvent;
          nextBtn.setAttribute('aria-disabled', !this.hasNextEvent);
        }
      });
    },

    updateCalendarHighlight() {
      if (
        this.currentOccurrenceIndex < 0
        || this.currentOccurrenceIndex >= this.appointments.length
      ) {
        return;
      }
      const currentOccurrenceData = this.appointments[this.currentOccurrenceIndex];
      const startAt = currentOccurrenceData?.start_at;
      if (startAt) {
        const dateStr = startAt.substring(0, 10);
        if (window.djlCalendar?.calendar?.setHighlightOnly) {
          window.djlCalendar.calendar.setHighlightOnly(dateStr);
        }
      }
    },

    setupEventRegistration(occurrence) {
      const registerBtn = document.getElementById('register_event');
      if (registerBtn) {
        registerBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleButtonClick(occurrence, registerBtn);
        });
      }

      const withdrawBtn = document.getElementById('withdraw_event');
      if (withdrawBtn) {
        withdrawBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleButtonClick(occurrence, withdrawBtn);
        });
      }
    },

    handleButtonClick(occurrence, button) {
      if (button.id === 'register_event') {
        this.askConfirmRegistration(occurrence, button);
      } else if (button.id === 'withdraw_event') {
        this.askConfirmWithdrawal(occurrence, button);
      }
    },

    askConfirmRegistration(occurrence, button) {
      if (occurrence.isRegistered) {
        this.showRegistrationMessage('You are already registered for this event.', 'error');
        return;
      }

      // eslint-disable-next-line no-alert
      if (confirm('REGISTER ATTENDANCE\n\nI will be attending this event.')) {
        this.registerForEvent(occurrence, button);
      }
    },

    askConfirmWithdrawal(occurrence, button) {
      // eslint-disable-next-line no-alert
      if (confirm('WITHDRAW ATTENDANCE\n\nI will not be attending this event.')) {
        this.withdrawFromEvent(occurrence, button);
      }
    },

    registerForEvent(occurrence, button) {
      const eventId = occurrence.event_id;
      const occurrenceId = occurrence.id;
      const { attendeeId } = button.dataset;

      if (!eventId) return;

      const buttonEl = button;
      const occurrenceData = occurrence;

      buttonEl.disabled = true;
      buttonEl.textContent = 'Registering...';

      const attendeeData = {
        attendee: {
          event_id: eventId,
          event_occurrence_id: occurrenceId,
          invite_status: 2, // accepted
          mandatory: false,
        },
      };

      let url = '/attendees';
      let method = 'POST';

      if (attendeeId && attendeeId !== 'null' && attendeeId !== 'undefined') {
        url = `/attendees/${attendeeId}`;
        method = 'PATCH';
        delete attendeeData.attendee.event_id;
        delete attendeeData.attendee.event_occurrence_id;
      } else if (occurrence.cancelledAttendeeId) {
        url = `/attendees/${occurrence.cancelledAttendeeId}`;
        method = 'PATCH';
        delete attendeeData.attendee.event_id;
        delete attendeeData.attendee.event_occurrence_id;
      }

      fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(attendeeData),
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          return response.json();
        })
        .then(data => {
          if (data.success) {
            buttonEl.id = 'withdraw_event';
            buttonEl.textContent = 'Withdraw';
            buttonEl.dataset.attendeeId = data.attendee.id;
            buttonEl.classList.remove('button--primary');
            buttonEl.classList.add('button--secondary');

            this.showRegistrationMessage('Successfully registered for event!', 'success');

            occurrenceData.isRegistered = true;

            if (occurrenceData.cancelledAttendeeId) {
              occurrenceData.cancelledAttendeeId = null;
            }
          } else {
            const errorMessage = data.errors && data.errors.length > 0
              ? data.errors[0].message
              : 'Failed to register for event. Please try again.';
            this.showRegistrationMessage(errorMessage, 'error');
            buttonEl.disabled = false;
            buttonEl.textContent = 'Register for Event';
          }
        })
        .catch((error) => {
          let errorMessage = 'An error occurred while registering. Please try again.';

          if (error.message.includes('HTTP 422')) {
            errorMessage = 'You may already be registered for this event or the event is full.';
          } else if (error.message.includes('HTTP 409')) {
            errorMessage = 'You are already registered for this event.';
          } else if (error.message.includes('HTTP 400')) {
            errorMessage = 'Invalid registration data. Please try again.';
          }

          this.showRegistrationMessage(errorMessage, 'error');
          buttonEl.disabled = false;
          buttonEl.textContent = 'Register for Event';
        });
    },

    withdrawFromEvent(occurrence, button) {
      const { attendeeId } = button.dataset;

      if (!attendeeId) return;

      const buttonEl = button;
      const occurrenceData = occurrence;

      buttonEl.disabled = true;
      buttonEl.textContent = 'Withdrawing...';

      const attendeeData = {
        attendee: {
          id: attendeeId,
          invite_status: 4, // cancelled
          cancellation_reason: 'User withdrew from event',
        },
      };

      fetch(`/attendees/${attendeeId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(attendeeData),
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          return response.json();
        })
        .then(data => {
          if (data.success) {
            const cancelledAttendeeId = buttonEl.dataset.attendeeId;

            buttonEl.id = 'register_event';
            buttonEl.textContent = 'Register for Event';
            buttonEl.dataset.attendeeId = null;
            buttonEl.classList.remove('button--secondary');
            buttonEl.classList.add('button--primary');

            this.showRegistrationMessage('Successfully withdrawn from event.', 'success');

            occurrenceData.isRegistered = false;

            occurrenceData.cancelledAttendeeId = cancelledAttendeeId;
          } else {
            const errorMessage = data.errors && data.errors.length > 0
              ? data.errors[0].message
              : 'Failed to withdraw from event. Please try again.';
            this.showRegistrationMessage(errorMessage, 'error');
            buttonEl.disabled = false;
            buttonEl.textContent = 'Withdraw';
          }
        })
        .catch(() => {
          this.showRegistrationMessage('An error occurred while withdrawing. Please try again.', 'error');
          buttonEl.disabled = false;
          buttonEl.textContent = 'Withdraw';
        });
    },

    _setupFocusTrap(modalElement) {
      if (typeof this._focusTrapCleanup === 'function') {
        this._focusTrapCleanup();
        this._focusTrapCleanup = null;
      }
      this._focusTrapCleanup = attachFocusTrap(modalElement);
    },

    showRegistrationMessage(message, type) {
      const messageEl = document.createElement('div');
      messageEl.className = `registration-message registration-message--${type}`;
      messageEl.textContent = message;

      const eventActions = document.querySelector('.event-actions');
      if (eventActions) {
        eventActions.appendChild(messageEl);

        setTimeout(() => {
          if (messageEl.parentNode) {
            messageEl.parentNode.removeChild(messageEl);
          }
        }, 5000);
      }
    },
  },
};
</script>
